/**
 * 移动端性能优化工具
 * 用于检测设备性能并提供降级策略
 */
import React from 'react';

/**
 * 设备性能等级枚举
 */
export const DevicePerformanceLevel = {
  HIGH: 'high',       // 高端设备：旗舰手机、平板
  MEDIUM: 'medium',   // 中端设备：普通智能手机
  LOW: 'low',         // 低端设备：老旧手机、低配设备
};

/**
 * 检测是否为移动端设备
 */
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * 检测是否为低性能设备
 * @returns {Object} { isLowEnd: boolean, level: string, reason: string }
 */
export const detectLowEndDevice = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isAndroid = ua.includes('android');
  const isIOS = /iphone|ipad|ipod/.test(ua);

  // Android 设备性能检测
  if (isAndroid) {
    // 检测是否为低内存设备 (< 4GB RAM 推断)
    const ram = navigator.deviceMemory || 4;
    const isLowRam = ram < 4;

    // 检测 GPU 能力（通过 WebGL）
    let gpuTier = 'unknown';
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl) {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();
          // 检测低端 GPU
          if (
            renderer.includes('mali-4') ||
            renderer.includes('mali-t') ||
            renderer.includes('adreno 3') ||
            renderer.includes('adreno 4') ||
            renderer.includes('powervr') ||
            renderer.includes('videocore') ||
            renderer.includes('intel hd graphics')
          ) {
            gpuTier = 'low';
          } else if (
            renderer.includes('adreno 5') ||
            renderer.includes('adreno 6') ||
            renderer.includes('mali-g') ||
            renderer.includes('apple gpu')
          ) {
            gpuTier = 'high';
          }
        }
      }
    } catch (e) {
      // WebGL 不可用
    }

    const cores = navigator.hardwareConcurrency || 4;
    const isLowCPU = cores <= 4;

    // 综合判断
    if (isLowRam || gpuTier === 'low' || (isLowCPU && isLowRam)) {
      return {
        isLowEnd: true,
        level: gpuTier === 'low' ? DevicePerformanceLevel.LOW : DevicePerformanceLevel.MEDIUM,
        reason: isLowRam ? '低内存设备' : gpuTier === 'low' ? '低端GPU' : '低CPU核心数',
      };
    }
  }

  // iOS 设备检测
  if (isIOS) {
    // 通过屏幕分辨率和像素比推断设备类型
    const screenWidth = window.screen.width;
    const pixelRatio = window.devicePixelRatio || 1;
    const totalPixels = screenWidth * pixelRatio;

    // iPhone 6/7/8 及以下视为低性能
    if (screenWidth <= 375 || totalPixels <= 750000) {
      return {
        isLowEnd: true,
        level: DevicePerformanceLevel.LOW,
        reason: '老旧iOS设备',
      };
    }
  }

  return {
    isLowEnd: false,
    level: DevicePerformanceLevel.HIGH,
    reason: '正常设备',
  };
};

/**
 * 应用性能降级策略
 */
export const applyPerformanceOptimization = () => {
  const device = detectLowEndDevice();
  const isMobileDevice = isMobile();

  if (!isMobileDevice) {
    // 桌面端不做任何修改
    return { optimized: false, reason: '桌面端' };
  }

  const optimizations = [];

  // 1. 降低 backdrop-filter blur 半径
  if (device.level !== DevicePerformanceLevel.HIGH) {
    const style = document.createElement('style');
    style.id = 'mobile-perf-optimization';

    if (device.level === DevicePerformanceLevel.LOW) {
      // 极低性能设备：完全禁用 backdrop-filter
      style.textContent = `
        *, *::before, *::after {
          -webkit-backdrop-filter: none !important;
          backdrop-filter: none !important;
        }
        canvas {
          display: none !important;
        }
        @keyframes * {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
        }
      `;
      optimizations.push('禁用 backdrop-filter 和 Canvas 动画');
    } else {
      // 中等性能设备：降低 blur 半径
      style.textContent = `
        * {
          -webkit-backdrop-filter: blur(4px) !important;
          backdrop-filter: blur(4px) !important;
        }
        .MuiPaper-root, .MuiCard-root {
          -webkit-backdrop-filter: blur(2px) !important;
          backdrop-filter: blur(2px) !important;
        }
        @keyframes borderGlow { 0%, 100% { opacity: 0.2; } 50% { opacity: 0.3; } }
        @keyframes float { 0%, 100% { transform: none; } 50% { transform: none; } }
        @keyframes glowPulse { 0%, 100% { box-shadow: none; } 50% { box-shadow: none; } }
        @keyframes btnBreatheGlow { 0%, 100% { box-shadow: none; } 50% { box-shadow: none; } }
        @keyframes btnSecondaryBreathe { 0%, 100% { box-shadow: none; } 50% { box-shadow: none; } }
        @keyframes orbit { 0%, 100% { opacity: 0; } 50% { opacity: 0; } }
      `;
      optimizations.push('降低 backdrop-filter blur 半径，简化动画');
    }

    document.head.appendChild(style);
  }

  // 2. 禁用 Web Animations API 的复杂动画
  if (device.level === DevicePerformanceLevel.LOW) {
    // 暂停所有 requestAnimationFrame 驱动的 Canvas 动画
    const originalRAF = window.requestAnimationFrame;
    window.requestAnimationFrame = (callback) => {
      // 只允许非常有限的帧率（5fps）来保持基本功能
      return originalRAF((timestamp) => {
        if (!window._lastRAFTime || timestamp - window._lastRAFTime > 200) {
          window._lastRAFTime = timestamp;
          callback(timestamp);
        }
      });
    };
    optimizations.push('限制 Canvas 动画帧率至 5fps');
  }

  // 3. 禁用图片懒加载的提前加载
  if ('connection' in navigator) {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      // 慢速网络下禁用动画
      if (connection.saveData || connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g') {
        const style = document.createElement('style');
        style.textContent = `
          canvas { display: none !important; }
          .mobile-skeleton { animation: none !important; }
        `;
        document.head.appendChild(style);
        optimizations.push('慢速网络：禁用动画');
      }
    }
  }

  return {
    optimized: true,
    level: device.level,
    reason: device.reason,
    optimizations,
  };
};

/**
 * React Hook: 获取当前设备性能等级
 */
export const useDevicePerformance = () => {
  const [device, setDevice] = React.useState(() => detectLowEndDevice());

  React.useEffect(() => {
    // 组件挂载时自动应用优化
    const result = applyPerformanceOptimization();
    setDevice(result);
  }, []);

  return device;
};
